import { useMemo } from 'react'

import {
  isClusterKey,
  isSourceKey,
  LIVE_OVERLAP_STYLE,
  NO_VOTE_COLOR,
  NO_VOTE_LABEL,
  shortLabel,
  SILENCE_STYLE,
  type SpeakerStyle,
  type SpeakerStyleMap,
} from './colors'

import styles from './Panels.module.scss'

import Button from '@/components/Button'
import DimensionIcon from '@/pages/app/AttributionObservability/DimensionIcon'
import {
  consolidateSpeakerMappings,
  type SpeakerMapping,
  type UnassignedPerson,
} from '@/pages/app/AttributionViewer/artifactDerivations'

const platformKey = (platform: string): string => {
  const normalized = platform
    .trim()
    .toLocaleLowerCase()
    .replace(/[\s-]+/g, '_')
  if (normalized === 'meet') return 'google_meet'
  if (normalized === 'microsoft_teams' || normalized === 'ms_teams') return 'teams'
  return normalized
}

const platformLabel = (platform: string): string => {
  const key = platformKey(platform)
  if (key === 'google_meet') return 'Google Meet'
  if (key === 'teams') return 'Microsoft Teams'
  if (key === 'zoom') return 'Zoom'
  return platform
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toLocaleUpperCase())
}

const osLabel = (os: string | null): string | null => {
  if (os === 'macos') return 'macOS'
  if (os === 'windows') return 'Windows'
  return null
}

type AssignmentMechanism = SpeakerMapping['assignmentSources'][number]['mechanism']

// Names the evidence that won the speaker id, not the slot it is stored in.
const mechanismLabel = (mechanism: AssignmentMechanism): string => {
  if (mechanism === 'user') return 'Set in app'
  if (mechanism === 'dom') return 'AX tree'
  if (mechanism === 'mic') return 'Mic or system channel'
  return 'Guessed from transcript'
}

const mechanismHint = (mechanism: AssignmentMechanism): string => {
  if (mechanism === 'user') return 'Someone picked this person in the app (slot: user)'
  if (mechanism === 'dom') return 'The meeting app showed them speaking at these times'
  if (mechanism === 'mic')
    return "Matched by the channel this voice came in on: the owner's own mic, or system audio when the invite had exactly one other guest (slot: mic)"
  return 'The AI matched them from the refined transcript (slot: llm)'
}

// Leads with how well corroborated the name is, since that is what the viewer exists to judge.
const identityConfidenceLabel = (origin: string, platform: string | null): string => {
  const platformSuffix = platform ? ` · ${platformLabel(platform)}` : ''
  if (origin === 'user') return 'Set in the app'
  if (origin === 'platform') return `From meeting roster${platformSuffix}`
  if (origin === 'merged_cal_platform') return `From calendar + roster${platformSuffix}`
  if (origin === 'calendar') return 'From calendar invite'
  if (origin === 'self') return 'Meeting owner'
  if (origin === 'llm') return 'Guessed from transcript'
  if (origin === 'mention') return '@-mentioned in notes'
  return 'Unknown source'
}

type Props = {
  hasLiveOverlap: boolean
  hasObservations: boolean
  observationPlatform: string | null
  observationOs?: string | null
  onRetrySpeakerMap: () => void
  speakerMappings: SpeakerMapping[]
  speakerMapState: 'loading' | 'present' | 'absent' | 'error'
  speakerStyles: SpeakerStyleMap
  unassignedPeople: UnassignedPerson[]
}

const Legend = ({
  hasLiveOverlap,
  hasObservations,
  observationPlatform,
  observationOs = null,
  onRetrySpeakerMap,
  speakerMappings,
  speakerMapState,
  speakerStyles,
  unassignedPeople,
}: Props) => {
  const byColorKey = new Map<string, SpeakerStyle>()
  for (const style of Array.from(speakerStyles.values())) {
    const current = byColorKey.get(style.colorKey)
    if (!current || current.label.includes('Speaker ')) byColorKey.set(style.colorKey, style)
  }
  const mappedColorKeys = new Set(speakerMappings.map((mapping) => mapping.colorKey))
  const speakerEntries = Array.from(byColorKey.values()).filter(
    (style) => !isClusterKey(style.colorKey) && !isSourceKey(style.colorKey) && !mappedColorKeys.has(style.colorKey)
  )
  const clusterEntries = Array.from(byColorKey.values()).filter(
    (style) =>
      isClusterKey(style.colorKey) &&
      !style.colorKey.startsWith('cluster:chorus:') &&
      !style.colorKey.startsWith('cluster:live:')
  )
  const chorusEntries = Array.from(byColorKey.values()).filter((style) => style.colorKey.startsWith('cluster:chorus:'))
  const liveEntries = Array.from(byColorKey.values()).filter((style) => style.colorKey.startsWith('cluster:live:'))
  const consolidatedMappings = useMemo(() => consolidateSpeakerMappings(speakerMappings), [speakerMappings])

  return (
    <div className={styles.legend} aria-label="Speaker identity legend">
      <div className={styles.legendHeader}>
        <h6>Legend</h6>
        {observationPlatform && (
          <span className={styles.platformSource}>
            {osLabel(observationOs) && <>{osLabel(observationOs)} · </>}
            <DimensionIcon dimension="platform" value={platformKey(observationPlatform)} size={14} />
            {platformLabel(observationPlatform)}
          </span>
        )}
      </div>
      {speakerMapState === 'loading' ? (
        <div className={styles.mappingStatus}>Loading persisted speaker mapping…</div>
      ) : speakerMapState === 'error' ? (
        <div className={styles.mappingStatus} role="alert">
          Could not load the persisted speaker mapping.
          <Button title="Retry" type="light" onClick={onRetrySpeakerMap} />
        </div>
      ) : speakerMappings.length > 0 ? (
        <div className={styles.mappingGrid} role="group" aria-label="Persisted speaker mappings">
          {consolidatedMappings.map((mapping) => {
            const style = byColorKey.get(mapping.colorKey)
            const speakersLabel = `Speaker${mapping.speakerIds.length > 1 ? 's' : ''} ${mapping.speakerIds.join(', ')}`
            return (
              <div
                key={mapping.speakerIds.join('-')}
                className={styles.mappingItem}
                role="group"
                aria-label={`${speakersLabel} mapping`}
              >
                <span
                  className={styles.legendSwatch}
                  data-pattern={style?.pattern ?? 'solid'}
                  style={{ backgroundColor: style?.color ?? NO_VOTE_COLOR }}
                />
                <div className={styles.mappingContent}>
                  <div className={styles.mappingIdentity}>
                    <span className={styles.mappingSpeaker}>{speakersLabel}</span>
                    <span className={styles.mappingArrow} aria-hidden="true">
                      →
                    </span>
                    <span className={mapping.name ? styles.mappingName : styles.mappingMissing}>
                      {mapping.name ?? 'Unmapped'}
                    </span>
                    {mapping.origin && (
                      <span className={styles.mappingCreation} title={`Stored origin: ${mapping.origin}`}>
                        {identityConfidenceLabel(mapping.origin, mapping.platform)}
                      </span>
                    )}
                  </div>
                  {mapping.resolvedSources.length > 0 && (
                    <div
                      className={styles.mappingAssignments}
                      role="group"
                      aria-label={`${speakersLabel} assignment sources`}
                    >
                      {mapping.resolvedSources.map((source) => (
                        <span
                          key={source.mechanism}
                          className={styles.mappingAssignmentResolved}
                          title={mechanismHint(source.mechanism)}
                        >
                          <span>{mechanismLabel(source.mechanism)}</span>
                          <span aria-hidden="true">→</span>
                          <span>{source.name ?? source.personId ?? 'Resolved speaker'}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className={styles.mappingStatus}>
          {speakerMapState === 'absent'
            ? 'No persisted speaker map is available.'
            : 'No speaker assignments persisted.'}
        </div>
      )}
      {unassignedPeople.length > 0 && (
        <details className={styles.unassignedPeople} role="group" aria-label="Unassigned people">
          <summary className={styles.unassignedPeopleSummary}>
            Unassigned people <span>({unassignedPeople.length})</span>
          </summary>
          <div className={styles.unassignedPeopleList}>
            {unassignedPeople.map((person) => (
              <span key={person.personId} className={styles.unassignedPerson}>
                <span>{person.name}</span>
                <span className={styles.unassignedPersonSource} title={`Stored origin: ${person.origin}`}>
                  {identityConfidenceLabel(person.origin, person.platform)}
                </span>
              </span>
            ))}
          </div>
        </details>
      )}
      {[
        {
          id: 'speakers',
          label: speakerMappings.length > 0 ? 'Other' : 'Speakers',
          entries: speakerEntries,
        },
        { id: 'live', label: 'Live clusters', entries: liveEntries },
        { id: 'overlap', label: 'Live overlap', entries: hasLiveOverlap ? [LIVE_OVERLAP_STYLE] : [] },
        { id: 'clusters', label: 'Soniox', entries: clusterEntries },
        { id: 'chorus', label: 'Chorus', entries: chorusEntries },
      ].map(
        (group) =>
          group.entries.length > 0 && (
            <div key={group.id} className={styles.legendRow} role="group" aria-label={`${group.label} legend`}>
              <span className={styles.legendLaneLabel}>{group.label}</span>
              <div className={styles.legendLaneItems}>
                {group.entries.map((speaker, index) => (
                  <span key={index} className={styles.legendItem}>
                    <span
                      className={styles.legendSwatch}
                      data-pattern={speaker.pattern}
                      style={{ backgroundColor: speaker.color, backgroundImage: speaker.backgroundImage }}
                    />
                    {shortLabel(speaker)}
                  </span>
                ))}
              </div>
            </div>
          )
      )}
      {hasObservations && (
        <div className={styles.legendRow} role="group" aria-label="AX tree signal legend">
          <span className={styles.legendLaneLabel}>AX tree</span>
          <div className={styles.legendLaneItems}>
            <span className={styles.legendItem} title="The AX tree reported no active speaker">
              <span
                className={styles.legendSwatch}
                data-pattern={SILENCE_STYLE.pattern}
                style={{ backgroundColor: SILENCE_STYLE.color }}
              />
              Silence
            </span>
            <span className={styles.legendItem}>Blank · No signal</span>
          </div>
        </div>
      )}
      <div className={styles.legendRow} role="group" aria-label="Unassigned attribution legend">
        <span className={styles.legendLaneLabel}>Unassigned</span>
        <div className={styles.legendLaneItems}>
          <span className={styles.legendItem}>
            <span
              className={`${styles.legendSwatch} ${styles.legendSwatchNoVote}`}
              style={{ backgroundColor: NO_VOTE_COLOR }}
            />
            {NO_VOTE_LABEL}
          </span>
        </div>
      </div>
    </div>
  )
}

export default Legend
